import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Edit2, Plus, Search, Trash2, X } from 'lucide-react';
import { useState } from 'react';

import { Button, Input, Select, Spinner, EmptyState } from '@terra/ui';

import { api } from '../../lib/api';

interface Props {
  deal: any;
  onClose: () => void;
}

export function FlashDealProductsManager({ deal, onClose }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [discountType, setDiscountType] = useState<'PERCENTAGE' | 'FLAT'>('PERCENTAGE');
  const [discountAmount, setDiscountAmount] = useState('');
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editDiscountType, setEditDiscountType] = useState<'PERCENTAGE' | 'FLAT'>('PERCENTAGE');
  const [editDiscountAmount, setEditDiscountAmount] = useState('');

  const { data: productsData, isLoading } = useQuery({
    queryKey: ['flash-deal-products', deal.id],
    queryFn: () => api.marketing.listFlashDealProducts(deal.id),
  });

  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['catalog-search', search],
    queryFn: () => api.products.catalog({ q: search, pageSize: 5 }),
    enabled: search.length > 2,
  });

  const addMutation = useMutation({
    mutationFn: (data: {
      productId: string;
      discountType: 'PERCENTAGE' | 'FLAT';
      discountAmount: number;
    }) => api.marketing.addFlashDealProduct(deal.id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flash-deal-products', deal.id] });
      void queryClient.invalidateQueries({ queryKey: ['flash-deals'] });
      setSelectedProduct(null);
      setDiscountAmount('');
      setSearch('');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (productId: string) => api.marketing.removeFlashDealProduct(deal.id, productId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flash-deal-products', deal.id] });
      void queryClient.invalidateQueries({ queryKey: ['flash-deals'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      productId,
      discountType,
      discountAmount,
    }: {
      productId: string;
      discountType: 'PERCENTAGE' | 'FLAT';
      discountAmount: number;
    }) =>
      api.marketing.updateFlashDealProduct(deal.id, productId, { discountType, discountAmount }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flash-deal-products', deal.id] });
      void queryClient.invalidateQueries({ queryKey: ['flash-deals'] });
      setEditingProductId(null);
      setEditDiscountAmount('');
    },
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProduct || !discountAmount) return;
    addMutation.mutate({
      productId: selectedProduct.id,
      discountType,
      discountAmount: Number(discountAmount),
    });
  };

  const products = productsData || [];

  const startEdit = (fp: any) => {
    setEditingProductId(fp.productId);
    setEditDiscountType(fp.discountType);
    setEditDiscountAmount(String(fp.discountAmount));
  };

  const cancelEdit = () => {
    setEditingProductId(null);
    setEditDiscountAmount('');
  };

  const saveEdit = (productId: string) => {
    const amount = Number(editDiscountAmount);
    if (!amount || amount <= 0) return;
    updateMutation.mutate({
      productId,
      discountType: editDiscountType,
      discountAmount: amount,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-surface flex h-full max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl shadow-xl">
        <div className="border-border/60 flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-ink text-lg font-bold">Manage Products</h2>
            <p className="text-ink-muted text-sm">{deal.title}</p>
          </div>
          <button
            onClick={onClose}
            className="bg-surface-subtle text-ink-muted hover:text-ink flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Add Product Form */}
          <div className="border-border/60 bg-surface-subtle/30 mb-8 rounded-xl border p-5">
            <h3 className="text-ink mb-4 text-[14px] font-bold">Add Product to Deal</h3>
            <form onSubmit={handleAdd} className="grid gap-4 md:grid-cols-4">
              <div className="relative md:col-span-2">
                <Input
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setSelectedProduct(null);
                  }}
                />
                {search.length > 2 && searchResults?.data && !selectedProduct && (
                  <div className="border-border/60 bg-surface absolute left-0 right-0 top-full z-10 mt-1 max-h-60 overflow-y-auto rounded-lg border shadow-lg">
                    {searchLoading ? (
                      <div className="p-4 text-center">
                        <Spinner size="sm" />
                      </div>
                    ) : searchResults.data.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">No products found</div>
                    ) : (
                      searchResults.data.map((p: any) => (
                        <button
                          key={p.id}
                          type="button"
                          className="hover:bg-surface-subtle text-ink w-full px-4 py-2 text-left text-sm"
                          onClick={() => {
                            setSelectedProduct(p);
                            setSearch(p.title);
                          }}
                        >
                          {p.title}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div>
                <Select
                  value={discountType}
                  onValueChange={(val) => setDiscountType(val as any)}
                  options={[
                    { label: 'Percentage (%)', value: 'PERCENTAGE' },
                    { label: 'Flat Amount', value: 'FLAT' },
                  ]}
                />
              </div>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Amt"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                  required
                  min="1"
                />
                <Button
                  type="submit"
                  disabled={!selectedProduct || !discountAmount || addMutation.isPending}
                >
                  {addMutation.isPending ? <Spinner size="sm" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
            </form>
          </div>

          {/* Assigned Products List */}
          <div>
            <h3 className="text-ink mb-4 text-[14px] font-bold">
              Assigned Products ({products.length})
            </h3>
            {isLoading ? (
              <div className="py-12 text-center">
                <Spinner size="lg" />
              </div>
            ) : products.length === 0 ? (
              <EmptyState
                icon={<Search className="text-ink-muted h-8 w-8" />}
                title="No products assigned"
                description="Search and add products to this flash deal."
              />
            ) : (
              <div className="border-border/60 rounded-xl border">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                      <th className="py-3 pl-4 pr-4">Product</th>
                      <th className="py-3 pr-4">Discount</th>
                      <th className="py-3 pr-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-border/40 divide-y text-[13px]">
                    {products.map((fp: any) => {
                      const editing = editingProductId === fp.productId;
                      return (
                        <tr key={fp.productId} className="hover:bg-surface-subtle/30">
                          <td className="py-3 pl-4 pr-4 font-medium">
                            {fp.product?.title || fp.productId}
                          </td>
                          <td className="py-3 pr-4">
                            {editing ? (
                              <div className="grid max-w-xs grid-cols-[minmax(120px,1fr)_90px] gap-2">
                                <Select
                                  value={editDiscountType}
                                  onValueChange={(val) =>
                                    setEditDiscountType(val as 'PERCENTAGE' | 'FLAT')
                                  }
                                  options={[
                                    { label: 'Percentage (%)', value: 'PERCENTAGE' },
                                    { label: 'Flat Amount', value: 'FLAT' },
                                  ]}
                                />
                                <Input
                                  type="number"
                                  value={editDiscountAmount}
                                  onChange={(e) => setEditDiscountAmount(e.target.value)}
                                  min="1"
                                  max={editDiscountType === 'PERCENTAGE' ? '100' : undefined}
                                />
                              </div>
                            ) : (
                              <span className="bg-brand-primary/10 text-brand-primary rounded-md px-2 py-1 text-xs font-bold">
                                {fp.discountType === 'PERCENTAGE'
                                  ? `${fp.discountAmount}%`
                                  : `₹${fp.discountAmount}`}
                              </span>
                            )}
                          </td>
                          <td className="py-3 pr-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {editing ? (
                                <>
                                  <button
                                    onClick={() => saveEdit(fp.productId)}
                                    disabled={!editDiscountAmount || updateMutation.isPending}
                                    className="text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                                    title="Save discount"
                                  >
                                    {updateMutation.isPending ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <Check className="h-4 w-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    disabled={updateMutation.isPending}
                                    className="text-ink-muted hover:text-ink disabled:opacity-50"
                                    title="Cancel edit"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => startEdit(fp)}
                                    disabled={removeMutation.isPending}
                                    className="text-ink-muted hover:text-ink disabled:opacity-50"
                                    title="Edit discount"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => removeMutation.mutate(fp.productId)}
                                    disabled={removeMutation.isPending}
                                    className="text-red-500 hover:text-red-600 disabled:opacity-50"
                                    title="Remove product"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
